<template>
    <div class="space-y-4">
        <h2 class="text-3xl! text-center">
            Kostenlosen Beratungstermin buchen
        </h2>

        <!-- STEP 1: Terminwahl -->
        <div
            v-if="bookingFormHidden"
            class="space-y-4"
        >
            <Calendar
                :deactivate-selection="deactivateSelection"
                @selected="onDateSelected"
            />

            <div class="grid grid-cols-3 lg:grid-cols-4 gap-4">
              <UButton
                    v-for="hour in currentHours"
                    :key="hour"
                    :label="hour"
                    :ui="{base: 'justify-center'}"
                    :variant="selectedHour === hour && !deactivateSelection ? 'solid' : 'outline'"
                    color="secondary"
                    @click="selectedHour === hour ? selectedHour = '' : selectedHour = hour"
              />
            </div>

            <p
                v-if="error"
                class="text-error-600 font-bold"
            >
                {{ error }}
            </p>

            <UCheckbox
                v-model="customDate"
                label="Ich wünsche eine individuelle Terminabsprache"
                style="padding-inline: 0 !important; padding-block: 0 !important;"
            />

          <UButton
                class="bordered w-full flex! justify-center items-center gap-2"
                color="secondary"
                trailing-icon="i-lucide-chevron-right"
                @click="verifySelection"
            >
                Kontaktdaten eintragen
          </UButton>
        </div>

        <!-- STEP 2: Kontaktdaten -->
        <form
            v-else
            class="space-y-4"
            @submit.prevent="sendMessage"
        >
            <div class="flex justify-between items-center font-bold text-primary">
                <span>{{ bookingDate }}</span>
                <span
                    class="text-xs underline cursor-pointer"
                    @click="bookingFormHidden = true"
                >
                    ändern
                </span>
            </div>

            <UFormField
                v-if="!toEmail"
                label="Ihre Postleitzahl"
            >
                <UInput
                    v-model="zipLocal"
                    label="Postleitzahl"
                />
            </UFormField>

            <div class="grid grid-cols-2 gap-4">
                <UFormField
                    label="Vorname"
                    required
                >
                    <UInput
                        v-model="firstname"
                        placeholder="Vorname"
                        class="w-full"
                        :class="errorFirstname ? 'border-2 border-error-600 rounded' : ''"
                    />
                </UFormField>
                <UFormField
                    label="Nachname"
                    required
                >
                    <UInput
                        v-model="lastname"
                        placeholder="Nachname"
                        class="w-full"
                        :class="errorLastname ? 'border-2 border-error-600 rounded' : ''"
/>
                </UFormField>
            </div>

            <UFormField
                label="E-Mail"
                required
            >
                <UInput
                    v-model="email"
                    type="email"
                    placeholder="E-Mail"
                    class="w-full"
                    :class="errorEmail ? 'border-2 border-error-600 rounded' : ''"
                />
            </UFormField>
            <UFormField label="Telefon">
                <UInput
                    v-model="phone"
                    type="tel"
                    placeholder="Telefon"
                    class="w-full"
                />
            </UFormField>

            <UFormField label="Nachricht">
                <UTextarea
                    v-model="message"
                    placeholder="Nachricht"
                    class="w-full"
                />
            </UFormField>

            <UCheckbox
                v-model="privacy"
                required
                style="padding-inline: 0 !important; padding-block: 0 !important;"
                :class="errorPrivacy ? 'border-2 border-error-600 rounded' : ''"
            >
                <template #label>
                    Ich habe die <a
                    href="/Datenschutz/"
                    target="_blank"
                    class="text-primary"
                >Datenschutzhinweise</a> zur Kenntnis genommen.
                </template>
            </UCheckbox>

<!--
          <UButton
              class="w-full justify-center"
              color="secondary"
              label="Termin anfragen"
              trailing-icon="i-lucide-send"
          />
-->
            <button
                class="w-full bg-primary text-white flex! gap-2 justify-center py-2"
            >
                Termin anfragen
                <IconPaperPlane class="w-6 h-6 fill-white"/>
            </button>
        </form>
    </div>
</template>

<script setup lang="ts">
import IconChevronRight from '~/components/Icons/IconChevronRight.vue';
import IconPaperPlane from '~/components/Icons/IconPaperPlane.vue';
import Calendar from '~/components/Calendar.vue';

const props = defineProps<{
    zip?: string
    toEmail?: string
}>();

const emit = defineEmits<{(e: 'sent', payload: { success: boolean }): void
}>();

/* ---------------- state ---------------- */
const zipLocal = ref(props.zip ?? '');
const firstname = ref('');
const lastname = ref('');
const phone = ref('');
const email = ref('');
const message = ref('');
const privacy = ref(false);

const bookingFormHidden = ref(true);
const selectedDate = ref<Date | null>(null);
const selectedHour = ref('');
const bookingDate = ref('');
const customDate = ref(false);
const deactivateSelection = ref(false);
const error = ref('');
const errorFirstname = ref(false);
const errorLastname = ref(false);
const errorEmail = ref(false);
const errorPrivacy = ref(false);

const hours = ['08:00', '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00'];
const hoursFriday = ['08:00', '09:00', '10:00', '11:00', '12:00'];

const currentHours = computed(() => (selectedDate.value?.getDay() === 5 ? hoursFriday : hours));

function onDateSelected(date: Date) {
    selectedDate.value = date;
}

function verifySelection() {
    error.value = '';

    if (customDate.value) {
        bookingDate.value = 'Individuelle Terminabsprache';
        bookingFormHidden.value = false;
        return;
    }

    if (!selectedDate.value || !selectedHour.value) {
        error.value = 'Bitte Datum und Uhrzeit wählen';
        return;
    }

    function formatDateDE(date: Date) {
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(date);
    }

    bookingDate.value = `Terminwunsch: ${formatDateDE(selectedDate.value)} um ${selectedHour.value} Uhr`;

    bookingFormHidden.value = false;
}

function validation() {
    if (firstname.value === '') {
        errorFirstname.value = true;
        return false;
    }
    errorFirstname.value = false;

    if (lastname.value === '') {
        errorLastname.value = true;
        return false;
    }
    errorLastname.value = false;

    if (email.value === '') {
        errorEmail.value = true;
        return false;
    }
    errorEmail.value = false;

    if (!privacy.value) {
        errorPrivacy.value = true;
        return false;
    }
    errorPrivacy.value = false;

    return true;
}

async function sendMessage() {
    if (validation()) {
        const {public: pub} = useRuntimeConfig();

        const response = await $fetch<{ sent: boolean }>(`${pub.apiUrl}/api/contact`, {
            method: 'POST',
            body: {
                mode: 'booking',
                article: null,
                zip: zipLocal.value,
                bookingDate: bookingDate.value,
                toEmail: props.toEmail,
                email: email.value,
                phone: phone.value,
                firstname: firstname.value,
                lastname: lastname.value,
                message: message.value,
            },
        });

        const {gtag} = useGtag();
        gtag('event', 'conversion', {send_to: 'AW-428666490/YM8DCNvT46oDEPrcs8wB'});
        gtag('event', 'select_content', {
            content_type: 'Website mailto',
            content_id: props.toEmail,
        });

        emit('sent', {success: !!response?.sent});
    }
}
</script>
